import React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { useOffers } from "@nju/data-access-portal-offers";
import { useUserLoginState } from "@nju/data-access-portal-user";
import { redirectToOrchestratorState } from "@nju/feature-portal-orchestrator";
import { portalRoutes } from "@nju/routes";
import {
  A,
  Button,
  GeneralErrorNotification,
  Heading,
  Layout,
  Main,
  Separator,
  Text,
} from "@nju/ui";
import { getAmountDescription, tripodContent, TripodStep } from "./partials";
import { tripodBannerMachine } from "./tripodBanner.machine";

export function TripodBanner() {
  const router = useRouter();
  const { amount } = portalRoutes.potykacz.input(router.query).unwrap();
  const amountDescription = getAmountDescription(amount);
  const { mobile: mobileOffers } = useOffers();
  const { isLoggedIn } = useUserLoginState();
  const offer = mobileOffers.indefiniteOffers.find((offer) => {
    return offer.display;
  });

  const [{ context, matches }, send] = useMachine(tripodBannerMachine, {
    context: {
      isLoggedIn,
    },
    actions: {
      redirectToNextStep: () => {
        if (isLoggedIn) {
          if (context.multiOrderOrchestrator) {
            if (context.multiOrderOrchestrator.state === "ITEM_SELECTION") {
              return router.push(
                portalRoutes.czyZachowacNumer.get({
                  mode: "mobile",
                  oferta: offer?.offerId,
                }),
              );
            }
            if (context.multiOrderOrchestrator.state === "START") {
              return router.push(
                portalRoutes.wybierak.get({
                  flow: "multiOrder",
                  amount: amount || "1",
                }),
              );
            } else {
              return redirectToOrchestratorState({
                state: context.multiOrderOrchestrator.state,
                mode: "mobile",
                flow: "multiOrder",
              });
            }
          }

          return router.push(
            portalRoutes.wybierak.get({
              flow: "multiOrder",
              amount: amount || "1",
            }),
          );
        }

        if (!isLoggedIn) {
          return router.push(
            portalRoutes.zalozKonto.get({
              oferta: offer?.offerId,
              mode: "mobile",
              amount: amount || "1",
              flow: "multiOrder",
            }),
          );
        }

        return router.push(
          portalRoutes.mojeKonto.index.get({
            mode: "mobile",
          }),
        );
      },
    },
  });

  const handleGoToNextStep = React.useCallback(() => {
    send({ type: "SUBMIT" });
  }, [send]);

  if (matches("error")) {
    return (
      <GeneralErrorNotification
        callback={() => {
          window.location.reload();
        }}
      />
    );
  }

  return (
    <div className="from-tablet:flex from-tablet:justify-center mb-8">
      <Layout>
        <Main>
          <div className="flex">
            <Text
              size={{ "@onlyMobile": "m", "@fromTablet": "xl" }}
              weight="bold"
            >
              Wybrana oferta:{" "}
              <span className="text-cerulean pl-1">
                {amountDescription} w ofercie nju subskrypcja
              </span>
            </Text>
          </div>
          <Separator size="l" />
          <Heading
            className="mb-2 from-tablet:mb-6"
            font="ttfirs"
            tone="neutral"
            variant="h2"
            weight="bold"
          >
            kolejne kroki do złożenia zamówienia
          </Heading>
          <ul className="only-mobile:columns-1 from-tablet:flex-row">
            {tripodContent.steps.map(
              ({ iconName, header, firstRowText, secondRowText }, index) => {
                return (
                  <TripodStep badge={index + 1} iconName={iconName} key={index}>
                    <div className="font-bold text-size-18 pb-1">{header}</div>
                    <div className="text-size-14">{firstRowText}</div>
                    <div className="text-size-14">{secondRowText}</div>
                  </TripodStep>
                );
              },
            )}
          </ul>
          <Separator size="l" />
          <div className="flex justify-between only-mobile:gap-3 only-mobile:flex-col-reverse flex-row">
            <Link href={portalRoutes.index.get()} passHref={true}>
              <A
                size={{
                  "@onlyMobile": "s",
                }}
                variant="secondary"
              >
                powrót
              </A>
            </Link>
            <Button
              onPress={handleGoToNextStep}
              size={{
                "@onlyMobile": "s",
              }}
              variant="primary"
            >
              przejdź dalej
            </Button>
          </div>
        </Main>
      </Layout>
    </div>
  );
}
